import { useEffect, useRef, useState, useCallback } from "react";

/**
 * Returns a scroll progress value (0–1) based on an element's position in the viewport.
 * - 0 = element's top is at the bottom of the viewport (just entering)
 * - 1 = element's bottom is at the top of the viewport (just leaving)
 *
 * Useful for parallax, fade-on-scroll, scale-on-scroll, etc.
 */
export function useScrollProgress() {
  const ref = useRef<HTMLDivElement>(null);
  const [progress, setProgress] = useState(0);
  const ticking = useRef(false);

  const update = useCallback(() => {
    const el = ref.current;
    if (!el) return;

    const rect = el.getBoundingClientRect();
    const windowHeight = window.innerHeight;

    // Element top relative to viewport bottom → element bottom relative to viewport top
    const start = rect.top - windowHeight; // negative when element is in view
    const end = rect.bottom; // 0 when element has fully passed

    const total = rect.height + windowHeight;
    const raw = total > 0 ? -start / total : 0;
    setProgress(Math.max(0, Math.min(1, raw)));

    ticking.current = false;
  }, []);

  useEffect(() => {
    const onScroll = () => {
      if (!ticking.current) {
        ticking.current = true;
        requestAnimationFrame(update);
      }
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    // Initial measurement
    update();

    return () => window.removeEventListener("scroll", onScroll);
  }, [update]);

  return { ref, progress };
}
